import React from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";

import { useEditEntityMutation } from "@api/entity.api";
import { useEntity } from "@components/entity/useEntity";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { EditModalFooter } from "./EditModalFooter";

const updateNameFormSchema = z.object({
    name: z.string().min(1, "Name is required"),
});

type UpdateNameFormValues = z.infer<typeof updateNameFormSchema>;

const fields = createFormikFields<UpdateNameFormValues>();

interface Props {
    show: boolean;
    onClose(): void;
}

export const UpdateNameModal: React.FC<Props> = ({ show, onClose }) => {
    const entity = useEntity();
    const editEntity = useEditEntityMutation(entity);
    const { error } = useToaster();

    const formik = useFormik<UpdateNameFormValues>({
        initialValues: {
            name: entity.name ?? "",
        },
        validate: validateFormikWithZodSchema(updateNameFormSchema),
        onSubmit: submitHelper({
            handler: async ({ name }) => {
                try {
                    await editEntity.mutateAsync({ name });
                    onClose();
                } catch {
                    error("Failed to update profile");
                }
            },
        }),
    });

    return (
        <PlainModal show={show} onHide={onClose}>
            <StandardModalHeader>Update Name</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form>
                    <VStack gap={16}>
                        <fields.Text
                            name="name"
                            label="Name"
                            showErrorMessage={false}
                            size="xl"
                        />
                        <EditModalFooter
                            onClose={onClose}
                            isValid={formik.isValid}
                            isSubmitting={formik.isSubmitting}
                        />
                    </VStack>
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
